import { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from 'utils/@reduxjs/toolkit';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { useInjectReducer } from 'utils/redux-injectors';
import { baseQuery, endpoints } from 'utils/api/endpoints';
import { InvoiceApiResponse, InvoiceListParams } from 'types/invoice';

export interface InvoiceState { }

export const initialState: InvoiceState = {};

const slice = createSlice({
  name: 'invoices',
  initialState,
  reducers: {
    someAction(state, action: PayloadAction<any>) { },
  },
});

// Custom base query for blob responses
const blobBaseQuery = fetchBaseQuery({
  baseUrl: endpoints.baseUrl,
  prepareHeaders: headers => {
    const token = localStorage.getItem('token');
    if (token) {
      headers.set('authorization', `Bearer ${token}`);
    }
    return headers;
  },
});

export const invoiceApi = createApi({
  reducerPath: 'invoiceApi',
  baseQuery,
  tagTypes: ['Invoice'],
  endpoints: builder => ({
    getInvoices: builder.query<InvoiceApiResponse, InvoiceListParams>({
      query: params => {
        const searchParams = new URLSearchParams();

        if (params.page) searchParams.append('page', params.page.toString());
        if (params.limit) searchParams.append('limit', params.limit.toString());
        if (params['payment-status'])
          searchParams.append(
            'payment-status',
            params['payment-status'].toString(),
          );
        if (params.startDate)
          searchParams.append('startDate', params.startDate);
        if (params.endDate) searchParams.append('endDate', params.endDate);
        if (params.search) searchParams.append('search', params.search);

        return {
          url: `${endpoints.INVOICES}?${searchParams.toString()}`,
          method: 'GET',
        };
      },
      providesTags: ['Invoice'],
    }),
    downloadInvoicePdf: builder.mutation<Blob, { invoiceId: number }>({
      queryFn: async ({ invoiceId }) => {
        try {
          const token = localStorage.getItem('token');
          const response = await fetch(
            `${endpoints.baseUrl}${endpoints.INVOICE_PDF}/${invoiceId}/pdf?download=true`,
            {
              method: 'GET',
              headers: {
                Authorization: `Bearer ${token}`,
              },
            },
          );

          if (!response.ok) {
            throw new Error('Failed to download PDF');
          }

          const blob = await response.blob();
          return { data: blob };
        } catch (error) {
          return { error: { status: 'FETCH_ERROR', error: String(error) } };
        }
      },
    }),
  }),
});

export const { actions: InvoiceActions } = slice;

export const useInvoiceSlice = () => {
  useInjectReducer({
    key: invoiceApi.reducerPath,
    reducer: invoiceApi.reducer,
  });
  return {
    actions: InvoiceActions,
    useGetInvoicesQuery: invoiceApi.useGetInvoicesQuery,
    useLazyGetInvoicesQuery: invoiceApi.useLazyGetInvoicesQuery,
    useDownloadInvoicePdfMutation: invoiceApi.useDownloadInvoicePdfMutation,
  };
};
